import React, { useMemo } from "react";
import { Box, Typography, TextField } from "@mui/material";
import { format, startOfDay } from "date-fns";
import ObservationPrintCard from "./ObservationPrintCard";
import { filterReportObservations } from "./reportObservations";
import {
  pickStickers,
  gutterStickers,
  DecorativeSticker,
  GutterPlacement,
  STICKER_BLEND,
} from "./stickers";
import { HONEY, PLUM } from "./reportTheme";
import { IChild } from "./types/child";

interface ObservationsPrintViewProps {
  child: IChild;
  fromDate?: Date | null;
  /** When set, an editable summary field is shown on screen and printed as plain text. */
  summary?: string;
  onSummaryChange?: (summary: string) => void;
}

/** A tilted row of bees, used as a header and footer flourish. */
const StickerRow: React.FC<{ stickers: DecorativeSticker[]; size?: number }> = ({ stickers, size = 52 }) => (
  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    {stickers.map((sticker, i) => (
      <Box
        key={`${sticker.src}-${i}`}
        component="img"
        src={sticker.src}
        alt=""
        aria-hidden
        sx={{ width: size, height: size, transform: `rotate(${sticker.rotate}deg)`, mixBlendMode: STICKER_BLEND }}
      />
    ))}
  </Box>
);

/**
 * One margin column beside an observation card. Always rendered, even when
 * empty, so every card lines up.
 */
const GutterBee: React.FC<{ placement: GutterPlacement | null }> = ({ placement }) => (
  <Box
    className="report-gutter"
    aria-hidden
    sx={{
      flexShrink: 0,
      width: 96,
      display: { xs: 'none', md: 'flex' },
      alignItems: placement?.align ?? 'center',
      justifyContent: 'center',
    }}
  >
    {placement && (
      <Box
        component="img"
        src={placement.src}
        alt=""
        sx={{
          width: placement.size,
          height: placement.size,
          opacity: placement.opacity,
          transform: `rotate(${placement.rotate}deg)`,
          mixBlendMode: STICKER_BLEND,
        }}
      />
    )}
  </Box>
);

const ObservationsPrintView: React.FC<ObservationsPrintViewProps> = ({ child, fromDate, summary, onSummaryChange }) => {
  const from = fromDate ? startOfDay(fromDate) : undefined;
  const observations = filterReportObservations(child.observations, fromDate);

  // Seeded on the child so the decoration is stable: editing the summary never
  // reshuffles it, and the printout matches what is on screen.
  const headerStickers = useMemo(() => pickStickers(`${child.id}-header`, 3), [child.id]);
  const footerStickers = useMemo(() => pickStickers(`${child.id}-footer`, 5), [child.id]);
  const cardStickers = useMemo(() => pickStickers(`${child.id}-cards`, observations.length), [child.id, observations.length]);
  const gutters = useMemo(() => gutterStickers(child.id, observations.length), [child.id, observations.length]);

  const fullName = [
    child.registration.childFirstName,
    child.registration.childMiddleName,
    child.registration.childLastName,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          marginTop: 4,
          marginBottom: 3,
          padding: 3,
          borderRadius: 4,
          border: `3px solid ${HONEY.border}`,
          background: `linear-gradient(135deg, ${HONEY.light} 0%, ${HONEY.mid} 100%)`,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" sx={{ color: PLUM.soft, fontWeight: 700, letterSpacing: 1.5 }}>
            Bee Happy Preschool
          </Typography>
          <Typography variant="h4" sx={{ color: PLUM.main, fontWeight: 700 }}>
            {fullName}
          </Typography>
          <Typography variant="subtitle1" sx={{ color: PLUM.soft }}>
            {from
              ? `Learning journey since ${format(from, "d MMMM yyyy")}`
              : "Learning journey so far"}
          </Typography>
        </Box>
        <StickerRow stickers={headerStickers} size={64} />
      </Box>

      {summary && (
        <Box
          sx={{
            marginBottom: 3,
            padding: 3,
            borderRadius: 4,
            border: `2px dashed ${HONEY.border}`,
            backgroundColor: HONEY.light,
          }}
        >
          <Typography variant="h6" sx={{ color: PLUM.main, fontWeight: 700, marginBottom: 1 }}>
            How {child.registration.childFirstName} is getting on
          </Typography>
          <TextField
            className="no-print"
            multiline
            minRows={8}
            fullWidth
            value={summary}
            onChange={(event) => onSummaryChange?.(event.target.value)}
            sx={{ backgroundColor: '#fff', borderRadius: 2 }}
            helperText="Edit the summary before printing. It is saved in this browser for 1 month."
          />
          <Typography className="print-only" variant="body1" sx={{ whiteSpace: "pre-wrap", color: '#3b3b3b' }}>
            {summary}
          </Typography>
        </Box>
      )}

      <Box>
        {observations.length === 0 && (
          <Typography variant="body1" color="text.secondary">
            No observations to show{from ? ` on or after ${format(from, "yyyy-MMM-dd")}` : ""}.
          </Typography>
        )}
        {observations.map((observation: any, index: number) => (
          <Box
            key={observation.id}
            className="report-row"
            sx={{ display: 'flex', alignItems: 'stretch', gap: 1, marginBottom: 2 }}
          >
            <GutterBee placement={gutters[index]?.left ?? null} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ObservationPrintCard
                observation={observation}
                sticker={cardStickers[index]}
                sx={{ width: '100%', maxWidth: 'none' }}
              />
            </Box>
            <GutterBee placement={gutters[index]?.right ?? null} />
          </Box>
        ))}
      </Box>

      {observations.length > 0 && (
        <Box sx={{ marginTop: 4, textAlign: 'center' }}>
          <StickerRow stickers={footerStickers} size={46} />
          <Typography variant="body2" sx={{ color: PLUM.soft, fontWeight: 600, marginTop: 1 }}>
            Keep up the brilliant work, {child.registration.childFirstName}!
          </Typography>
          <Typography variant="caption" sx={{ color: PLUM.soft }}>
            Bee Happy Preschool &middot; Hedgerley SL2 3UU
          </Typography>
        </Box>
      )}

      <style>
        {`
        @media print {
          body {
            background: white !important;
          }
          .MuiCard-root {
            box-shadow: none !important;
            margin: 0 !important;
          }
          /* Keep each bee with the card it belongs to */
          .report-row {
            page-break-inside: avoid;
          }
          /* Margin bees are hidden on narrow screens, but paper always has room */
          .report-gutter {
            display: flex !important;
            width: 74px !important;
          }
          .report-gutter img {
            max-width: 62px !important;
            max-height: 62px !important;
          }
          /* Give the narrower print column back some room */
          .report-photo {
            width: 160px !important;
            height: 160px !important;
          }
        }
      `}
      </style>
    </Box>
  );
};

export default ObservationsPrintView;
